"use client";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePassportProvider } from "@/context";
import {
  Action,
  ControlType,
} from "@/context/AnalyticsProvider/AnalyticsProvider";
import {
  GamePageSection,
  useGamePageAnalytics,
} from "@/context/AnalyticsProvider/gamePage";
import { useGameDetailsContext } from "@/context/GameDetailsContext";
import { GameOnboardingProvider } from "@/context/GameOnboardingContext";
import { cn } from "@/lib/utils";
import { isSafari } from "@/utils/browser";
import { XIcon } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useState } from "react";
import { GlassButton } from "../GlassButton";
import { AnimatedEffect } from "./AnimatedEffect";
import { BronzeRewardAchieved } from "./BronzeRewardAchieved";
import { FinalReward } from "./FinalReward";
import { FollowOnX } from "./FollowOnX";
import { GPOnboardingLogin } from "./GPOnboardingLogin";
import { LinkSteam } from "./LinkSteam";
import { OnboardingExtrinsic } from "./OnboardingExtrinsic";
import { OnboardingHybrid } from "./OnboardingHybrid";
import { OnboardingInGame } from "./OnboardingInGame";
import { SilverRewardAchieved } from "./SilverRewardAchieved";
import { WishlistSteam } from "./WishlistSteam";
import { WishlistSteamVerify } from "./WishlistSteamVerify";
import { GPExperimentType, GPOnboardingStep } from "./utils";

interface GPOnboardingModalProps {
  open: boolean;
  onClose: () => void;
  experimentType?: GPExperimentType;
}

// Main Modal Component
export function GPOnboardingModal({
  open,
  onClose,
  experimentType,
}: GPOnboardingModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [hasCompletedOnboardingScreen, setHasCompletedOnboardingScreen] =
    useState(false);
  const [isWishlistVerified, setIsWishlistVerified] = useState(false);
  const { isLoggedIn, isLoggingIn, walletAddress } = usePassportProvider();
  const { track } = useGamePageAnalytics();
  const { addToWishlist } = useGameDetailsContext();

  useEffect(() => {
    if (walletAddress && open) {
      addToWishlist();
    }
  }, [walletAddress, addToWishlist, open]);

  // Determine if we should show onboarding splash screen
  const showSplashScreen = useMemo(() => {
    return (
      (experimentType === GPExperimentType.EXTRINSIC ||
        experimentType === GPExperimentType.HYBRID ||
        experimentType === GPExperimentType.INGAME ||
        experimentType === GPExperimentType.NO_PROMPT) &&
      !hasCompletedOnboardingScreen
    );
  }, [experimentType, hasCompletedOnboardingScreen]);

  useEffect(() => {
    if (!open) return;

    let controlStep = `step-${GPOnboardingScreens[currentStep].key}`;
    if (showSplashScreen) {
      controlStep = "step-get-started";
    }

    track({
      action: Action.VIEWED,
      extras: {
        control: controlStep.toLowerCase() as Lowercase<string>,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });
  }, [track, currentStep, showSplashScreen, open]);

  const handleNext = useCallback(
    (increment?: number) => {
      // If we're showing an onboarding screen and haven't completed it yet,
      // transition to normal flow instead of incrementing step
      if (showSplashScreen && !hasCompletedOnboardingScreen) {
        setHasCompletedOnboardingScreen(true);
        return;
      }

      // Skip Wishlist Verify step if wishlist is already verified
      // Current step 4 is SilverRewardAchieved. Next is 5 (Verify) or 6 (FollowOnX)
      if (currentStep === 4 && isWishlistVerified) {
        setCurrentStep((prev) => prev + 2);
        return;
      }

      setCurrentStep((prev) => prev + (increment ?? 1));
    },
    [
      showSplashScreen,
      hasCompletedOnboardingScreen,
      currentStep,
      isWishlistVerified,
    ],
  );

  const handleClose = useCallback(
    (open: boolean) => {
      // if modal becomes open from closed, ignore handleClose
      if (open) {
        track({
          action: Action.OPENED,
          extras: {
            control: "onboarding-modal",
          },
        });
        return;
      }

      track({
        action: Action.CLOSED,
        extras: {
          control: "onboarding-modal",
        },
      });

      setCurrentStep(0);
      onClose();
    },
    [onClose, track],
  );

  const GPOnboardingScreens = useMemo(
    () => [
      <GPOnboardingLogin
        key={GPOnboardingStep.LOGIN}
        onNext={handleNext}
        isVisible={currentStep === 0}
        track={track}
      />,
      <BronzeRewardAchieved
        key={GPOnboardingStep.BRONZE_REWARD_ACHIEVED}
        onNext={handleNext}
        isVisible={currentStep === 1}
        track={track}
      />,
      <WishlistSteam
        key={GPOnboardingStep.WISHLIST_STEAM}
        onNext={handleNext}
        isVisible={currentStep === 2}
        track={track}
      />,
      <LinkSteam
        key={GPOnboardingStep.LINK_STEAM}
        onNext={handleNext}
        isVisible={currentStep === 3}
        track={track}
        setIsWishlistVerified={setIsWishlistVerified}
      />,
      <SilverRewardAchieved
        key={GPOnboardingStep.SILVER_REWARD_ACHIEVED}
        onNext={handleNext}
        isVisible={currentStep === 4}
        track={track}
      />,
      <WishlistSteamVerify
        key={GPOnboardingStep.WISHLIST_STEAM_VERIFY}
        onNext={handleNext}
        isVisible={currentStep === 5}
        track={track}
      />,
      <FollowOnX
        key={GPOnboardingStep.FOLLOW_ON_X}
        onNext={handleNext}
        isVisible={currentStep === 6}
        track={track}
      />,
      <FinalReward
        key={GPOnboardingStep.GOLD_REWARD_ACHIEVED}
        onNext={onClose}
        isVisible={currentStep === 7}
        track={track}
      />,
    ],
    [handleNext, currentStep, onClose, track],
  );

  // Determine which onboarding splash screen to show when onboardingType is set
  const GPOnboardingSplashScreen = useCallback(() => {
    switch (experimentType) {
      case GPExperimentType.EXTRINSIC:
        return (
          <OnboardingExtrinsic onNext={handleNext} track={track} isVisible />
        );

      case GPExperimentType.HYBRID:
        return <OnboardingHybrid onNext={handleNext} track={track} isVisible />;

      case GPExperimentType.INGAME:
      case GPExperimentType.NO_PROMPT:
        return <OnboardingInGame onNext={handleNext} track={track} isVisible />;

      default:
        return null;
    }
  }, [experimentType, handleNext, track]);

  const handlePrizeImgClick = useCallback(() => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "prize",
        controlType: ControlType.IMAGE,
        section: GamePageSection.ONBOARDING_MODAL,
        controlLabel:
          currentStep >= 7
            ? "Gold Prize"
            : currentStep >= 4
              ? "Silver Prize"
              : currentStep >= 2
                ? "Bronze Prize"
                : "Default Prize",
      },
    });
  }, [track, currentStep]);

  const handleLogoClick = useCallback(() => {
    track({
      action: Action.PRESSED,
      extras: {
        control: "logo",
        controlType: ControlType.IMAGE,
        section: GamePageSection.ONBOARDING_MODAL,
        controlLabel: "8 Ball Logo",
      },
    });
  }, [track]);

  const handleProgressBarClick = useCallback(
    (label: string) => {
      track({
        action: Action.PRESSED,
        extras: {
          control: "progress-bar",
          controlLabel: label,
          section: GamePageSection.ONBOARDING_MODAL,
        },
      });
    },
    [track],
  );

  // Lock body scroll when modal is open since we are using modal={false}
  useEffect(() => {
    if (open) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [open]);

  const isVerifyStep = currentStep === 5;

  return (
    <GameOnboardingProvider>
      {/* Custom Overlay to prevent flickering when switching modal modes */}
      <div
        className={cn(
          "fixed inset-0 z-[99] bg-black/40 transition-opacity duration-200",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
          isLoggingIn && "pointer-events-none",
        )}
      />
      {/* modal={false} prevents the focus trap and allows outside interactions (Passport iframe) */}
      <Dialog open={open} onOpenChange={handleClose} modal={false}>
        <DialogContent
          className={cn(
            "font-suisse flex gap-y-0 flex-col !z-[100] bg-neutral-900/30 backdrop-blur-[32px] border-0",
            "h-[100dvh] w-screen max-h-none max-w-none !rounded-none", // Mobile full screen
            "md:glass-border md:h-[95vh] md:w-[95vw] md:!rounded-[2.5rem] md:max-h-[50rem] md:max-w-xl", // Desktop modal
            showSplashScreen
              ? "p-0 overflow-hidden"
              : cn(
                  "px-[clamp(0.5rem,12.5vw,5rem)] py-[clamp(1rem,6.25vh,3rem)]",
                  !isVerifyStep && "pb-[clamp(4rem,12vh,6rem)]", // Increased bottom padding for non-verify steps
                ),
          )}
          style={isLoggingIn ? { pointerEvents: "none" } : undefined}
          removeCloseButton
          // Hide the native overlay since we are using a custom one
          overlayClassName="hidden"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogTitle className="sr-only">
            Custom Steam Onboarding Modal
          </DialogTitle>

          {showSplashScreen ? (
            // Show onboarding splash screen
            <GPOnboardingSplashScreen />
          ) : (
            // Normal flow with all steps
            <>
              {!isVerifyStep && (
                <div className="relative mx-auto h-fit w-full">
                  <AnimatedEffect
                    className="absolute w-full h-full"
                    tint="#D37A38"
                  />
                  <div className="relative mx-auto w-full max-w-[16.125rem] h-[clamp(9rem,24vh,15.125rem)]">
                    <Image
                      src={
                        currentStep >= 7
                          ? "/assets/steam-onboarding/crate-gold.webp"
                          : currentStep >= 4
                            ? "/assets/steam-onboarding/crate-silver.webp"
                            : currentStep >= 2
                              ? "/assets/steam-onboarding/crate-bronze.webp"
                              : "/assets/steam-onboarding/prize-8ball-default.webp"
                      }
                      alt="8 Ball loot crate"
                      width={258}
                      height={242}
                      className="drop-shadow-2xl object-contain w-full h-full"
                      onClick={handlePrizeImgClick}
                    />
                  </div>

                  {!isLoggedIn ? (
                    <Image
                      width={330}
                      height={111}
                      src="/assets/steam-onboarding/logo.webp"
                      alt="Immutable 8 Ball Rewards"
                      className="-bottom-6 left-1/2 -translate-x-1/2 absolute w-3/5 max-w-[20.625rem] h-auto"
                      onClick={handleLogoClick}
                    />
                  ) : null}
                </div>
              )}
              {!isVerifyStep && (
                <div className="p-2 gap-x-0.5 flex items-center">
                  {/* First Progress Bar - Bronze */}
                  {/* biome-ignore lint/a11y/useKeyWithClickEvents: tracking only onclick */}
                  <div
                    className={cn(
                      "min-w-0 flex-1 relative h-2 rounded-[0.125rem] glass-container-border before:rounded-[0.125rem] before:!inset-0 before:!opacity-100 after:!opacity-0 transition-all duration-500",
                      currentStep >= 2
                        ? "bg-gradient-to-r from-[#D37A38] to-[#F5A623]"
                        : "bg-white/20",
                      currentStep === 1 && "animate-pulse",
                    )}
                    onClick={() => handleProgressBarClick("Segment 1")}
                  />
                  {/* Bronze Crate */}
                  <div className="relative max-w-11 w-[7.5vw]">
                    {currentStep === 1 && (
                      <div className="absolute inset-0 -m-2 animate-pulse">
                        <AnimatedEffect
                          className="absolute w-full h-full"
                          tint="#D37A38"
                        />
                      </div>
                    )}
                    <Image
                      src="/assets/steam-onboarding/crate-bronze.webp"
                      alt=""
                      width={44}
                      height={44}
                      className={cn(
                        "max-w-11 w-[7.5vw] relative z-10 transition-transform duration-300",
                        currentStep === 1 && "scale-110",
                      )}
                      onClick={() => handleProgressBarClick("Bronze Crate")}
                    />
                  </div>
                  {/* Second Progress Bar - Silver */}
                  {/* biome-ignore lint/a11y/useKeyWithClickEvents: tracking only onclick */}
                  <div
                    className={cn(
                      "min-w-0 flex-1 relative h-2 rounded-[0.125rem] glass-container-border before:rounded-[0.125rem] before:!inset-0 before:!opacity-100 after:!opacity-0 transition-all duration-500",
                      currentStep >= 4
                        ? "bg-gradient-to-r from-[#D37A38] to-[#F5A623]"
                        : "bg-white/20",
                    )}
                    onClick={() => handleProgressBarClick("Segment 2")}
                  />
                  {/* Silver Crate */}
                  <div className="relative max-w-11 w-[7.5vw]">
                    {currentStep === 4 && (
                      <div className="absolute inset-0 -m-2 animate-pulse">
                        <AnimatedEffect
                          className="absolute w-full h-full"
                          tint="#D37A38"
                        />
                      </div>
                    )}
                    <Image
                      src="/assets/steam-onboarding/crate-silver.webp"
                      alt=""
                      width={44}
                      height={44}
                      className={cn(
                        "max-w-11 w-[7.5vw] relative z-10 transition-transform duration-300",
                        currentStep === 4 && "scale-110",
                      )}
                      onClick={() => handleProgressBarClick("Silver Crate")}
                    />
                  </div>
                  {/* Third Progress Bar - Gold */}
                  {/* biome-ignore lint/a11y/useKeyWithClickEvents: tracking only onclick */}
                  <div
                    className={cn(
                      "min-w-0 flex-1 relative h-2 rounded-[0.125rem] glass-container-border before:rounded-[0.125rem] before:!inset-0 before:!opacity-100 after:!opacity-0 transition-all duration-500",
                      currentStep >= 7
                        ? "bg-gradient-to-r from-[#D37A38] to-[#F5A623]"
                        : "bg-white/20",
                      currentStep === 6 && "animate-pulse",
                    )}
                    onClick={() => handleProgressBarClick("Segment 3")}
                  />
                  {/* Gold Crate */}
                  <div className="relative max-w-11 w-[7.5vw]">
                    {currentStep === 6 && (
                      <div className="absolute inset-0 -m-2 animate-pulse">
                        <AnimatedEffect
                          className="absolute w-full h-full"
                          tint="#D37A38"
                        />
                      </div>
                    )}
                    <Image
                      src="/assets/steam-onboarding/crate-gold.webp"
                      alt=""
                      width={44}
                      height={44}
                      className={cn(
                        "max-w-11 w-[7.5vw] relative z-10 transition-transform duration-300",
                        currentStep === 6 && "scale-110",
                      )}
                      onClick={() => handleProgressBarClick("Gold Crate")}
                    />
                  </div>
                </div>
              )}

              {/* Onboarding Steps */}
              <div className="relative overflow-hidden flex-grow">
                <div
                  className={cn(
                    "flex h-full transition-transform duration-500 ease-in-out",
                    isVerifyStep ? "items-start md:items-center" : "",
                  )}
                  style={{
                    transform: `translateX(-${currentStep * 100}%)`,
                  }}
                >
                  {GPOnboardingScreens.map(
                    (Step: JSX.Element, index: number) => (
                      <div
                        key={Step.key}
                        className={cn(
                          "w-full h-full flex-shrink-0 transition-opacity duration-500 ease-in-out flex text-center",
                          index === currentStep ? "opacity-100" : "opacity-0",
                        )}
                        style={{
                          // safari hides auto content on blur incorrectly, revert to visible
                          contentVisibility: isSafari() ? "visible" : "auto",
                        }}
                      >
                        {Step}
                      </div>
                    ),
                  )}
                </div>
              </div>

              <DialogClose
                className="absolute right-4 top-4"
                onClick={onClose}
                asChild
              >
                <GlassButton
                  control="close-modal"
                  section={GamePageSection.ONBOARDING_MODAL}
                  className="size-10 bg-white/10"
                >
                  <XIcon className="size-4" />
                </GlassButton>
              </DialogClose>
            </>
          )}
        </DialogContent>
      </Dialog>
    </GameOnboardingProvider>
  );
}
